import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'gatsby';
import { useLocation } from 'react-use';
import { GoDotFill } from 'react-icons/go';

import { CartContext, NonCommerceInfoContext, ProductContainerContext } from '@rsc/contexts';
import type { SanitySiteSettings } from '@rsc/models/gatsby-graphql-types';
import { useUiComponentsInternationalContext } from '@rsc/static-translations/src';
import { cn } from '@rsc/tailwind-base/utils';
import { useProductDataLayer } from '@rsc/ui-components/hooks';
import { Button } from '@rsc/ui-components/shadcn/Button';
import Badge from '@rsc/ui-components/tailwind/Badge';
import Price from '@rsc/ui-components/tailwind/Price';
import ProductOptions from '@rsc/ui-components/tailwind/ProductOptions';
import LargeCartButton from '@rsc/ui-components/tailwind/LargeCartButton';
import { formatReviewSku, pushToDataLayer } from '@rsc/utils';
import { useAddToCartBtnTxt } from '@rsc/utils/variants';
import { isPixelSupported, pixelCategoryFromLocation, PIXELS } from '@rsc/utils/pixels';

interface Props {
  buttonFull?: boolean;
  siteSettings: SanitySiteSettings;
}

export default function ProductSidebar({
  buttonFull = true,
  siteSettings,
}: Readonly<Props>) {
  const location = useLocation();
  const { isNonCommerce } = useContext(NonCommerceInfoContext) as any;
  const {
    addOns,
    product,
    activeVariant,
    variants,
    quantity,
    sellingPlanGroups,
    pricing,
    selectedSellingPlan,
    subscribed,
    productLifecycle,
    isBazaarVoiceEnabled,
  } = useContext(ProductContainerContext) as any;
  const { addToCart } = useContext(CartContext) as any;
  const detailsRef = useRef(null);
  const [isAvailableForSale, setIsAvailableForSale] = useState(false);
  const [quantityAvailable, setQuantityAvailable] = useState(0);
  const { tt, currency } = useUiComponentsInternationalContext();
  const addToCartBtnTxt = useAddToCartBtnTxt();

  useProductDataLayer({
    product: product?.shopifyProduct,
    activeVariant,
  });

  const scrollToDetails = e => {
    e.preventDefault();
    if (detailsRef?.current) {
      const scrollTo =
        detailsRef.current.getBoundingClientRect().top + window.scrollY - 80;
      window.scroll({
        top: scrollTo,
        behavior: 'smooth',
      });
    }
  };

  const scrollToBazaarVoice = e => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof document !== 'undefined') {
      const bazaarVoiceElement = document.getElementById('bazaarVoice');
      const scrollTo =
        bazaarVoiceElement.getBoundingClientRect().top + window.scrollY - 100;

      window.scroll({
        top: scrollTo,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    setIsAvailableForSale(activeVariant?.availableForSale);
    setQuantityAvailable(activeVariant?.quantityAvailable);
  }, [activeVariant]);

  return (
    <div
      id="product-details"
      ref={detailsRef}
      className="flex flex-col items-start gap-4 pr-0 pb-0 lg:pb-16"
    >
      <h1 className="text-xl md:text-2xl leading-extraTight md:leading-extraTight mb-0">
        {product?.store.title}
      </h1>
      {sellingPlanGroups?.length && !isNonCommerce ? (
        <Badge
          sub
          discount={
            selectedSellingPlan?.percentageDiscount ??
            sellingPlanGroups[0].percentageDiscount
          }
        />
      ) : null}
      <div className="flex items-center content-center gap-2 self-stretch flex-wrap">
        {product?.badge?.text && (
          <Badge
            hex={product?.badge?.hex}
            message={product?.badge?.text}
            className="mr-2"
          />
        )}

        {isBazaarVoiceEnabled && (
          <Button
            variant="ghost"
            onClick={scrollToBazaarVoice}
            aria-label={tt(
              s =>
                s.productContainerDeptComponents.scrollToReviewsButtonAriaLabel
            )}
            data-bv-show="inline_rating"
            data-bv-product-id={formatReviewSku(
              product?.shopifyProduct?.variants?.[0]?.sku
            )}
            data-bv-seo="false"
            style={{ minHeight: 40 }}
          />
        )}
      </div>
      {!isNonCommerce && productLifecycle !== 'Obsolete' && (
        <Price product={product} pricing={pricing} subscribed={subscribed} />
      )}
      {product?.pdpAttributePairs?.length > 0 && (
        <div className="flex flex-col items-start self-stretch md:grid grid-cols-2 gap-x-6">
          {product.pdpAttributePairs.map(ap => (
            <div
              key={ap._key}
              className="flex self-stretch gap-1 py-2 border-b last:border-0 md:last:border-b"
            >
              <GoDotFill size="24" className="shrink-0 fill-brand" />
              <div className="flex items-start flex-col">
                <p className="text-sm mb-0">{ap?.name}</p>
                <p className="text-sm font-bold">{ap?.value}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      <ProductOptions buttonFull={buttonFull} siteSettings={siteSettings} />
      {/* Mobile only button at bottom of screen after scrolling */}
      {!isNonCommerce && productLifecycle !== 'Obsolete' && (
        <div
          style={{
            boxShadow:
              '0px -10px 15px -3px rgba(0, 0, 0, 0.10), 0px -4px 6px -2px rgba(0, 0, 0, 0.05)',
            zIndex: 2000,
          }}
          className={cn(
            'lg:hidden fixed bg-background py-4 px-6 mr-0 left-0 right-0 -bottom-px w-full',
            quantityAvailable > 0 ? 'block' : 'hidden'
          )}
        >
          {variants?.length > 1 || sellingPlanGroups?.length > 0 ? (
            <LargeCartButton
              aria-label={tt(
                s => s.productContainerDeptComponents.scrollBackAriaLabel
              )}
              className="w-full"
              title={tt(s => s.productContainerDeptComponents.addToCartButton)}
              variant="cta"
              onClick={scrollToDetails}
            >
              <Link to="#product-details" />
            </LargeCartButton>
          ) : (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {isAvailableForSale &&
              productLifecycle !== 'Coming Soon' &&
              productLifecycle !== 'Obsolete' ? (
                <LargeCartButton
                  type="button"
                  variant="cta"
                  className="w-full"
                  disabled={!isAvailableForSale}
                  aria-label={`${tt(
                    s => s.productContainerDeptComponents.addToCartButton
                  )} ${product?.shopifyProduct?.title}`}
                  title={tt(
                    s => s.productContainerDeptComponents.addToCartButton
                  )}
                  onClick={async event => {
                    // ! This only handles single variant non subscription products
                    event.preventDefault();
                    event.stopPropagation();

                    await addToCart({
                      activeVariant,
                      addOns,
                      product,
                      quantity,
                      selectedSellingPlan: null,
                      variantId: activeVariant.shopifyId,
                      variantMetafields: activeVariant?.metafields,
                    });

                    if (isPixelSupported(PIXELS.gtm)) {
                      const mappedAddonGAProducts =
                        addOns?.map(s => ({
                          quantity: s.cartProps.quantity,
                          variant: {
                            ...s,
                            title: s.cartProps.variantTitle,
                          },
                          product: {
                            title: s.cartProps.title,
                            productType: s.cartProps.category,
                            vendor: s.cartProps.brand,
                          },
                          isSubscription: false,
                          category: pixelCategoryFromLocation(location)
                        })) || [];

                      const GAItems = [
                        {
                          variant: activeVariant,
                          product: product?.shopifyProduct,
                          quantity,
                          isSubscription: false,
                          category: pixelCategoryFromLocation(location)
                        },
                        ...mappedAddonGAProducts,
                      ];

                      pushToDataLayer('add_to_cart', {
                        ecommerce: {
                          currency: currency,
                          value: GAItems.reduce(
                            (total, GAItem) =>
                              total + GAItem.quantity * GAItem.variant.price,
                            0.0
                          ),
                        },
                        items: GAItems,
                      });
                    }
                  }}
                />
              ) : (
                <Button
                  type="button"
                  variant="cta"
                  className="w-full"
                  disabled={productLifecycle === 'Coming Soon'}
                >
                  {addToCartBtnTxt(productLifecycle)}
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
